import React, { ReactNode } from 'react';
import {
  Box,
  CircularProgress,
  ScopedCssBaseline,
  Typography,
} from '@mui/material';
import { Theme, styled } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import {
  OLD_SIDEBAR_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_EXPANDED_WIDTH,
} from 'new/components/AppBar/Sidebar';
import { LocationHeaderProps } from 'new/components/LocationHeader';
import { PageLayout } from 'new/components/PageLayout';
import PageMessageTemplate, {
  MessageVariant,
} from 'new/components/PageMessageTemplate';
import { useSidebarStore } from 'new/hooks/useSidebarStore';
import { useGetUserFromToken } from 'new/modules/MembershipPlan/hooks';
import PageCardTemplate from 'new/templates/PageCardTemplate';

import Button from './Button/Button';
import { BREADCRUMBS_HEIGHT, TOPBAR_HEIGHT } from './Headers/EnrollHeader';
import GlobalBanner from './GlobalBanner';
import { TENANT } from 'new/tenants';
import ModalForceLogOut from './ModalForceLogOut';
import { useDisputesData } from 'new/components/Disputes/hooks';

type Props<T> = {
  children: ((data: T) => React.ReactNode) | React.ReactNode;
  sx?: SxProps<Theme>;
  isLoading?: boolean;
  topBar?: 'setup' | 'enroll';
  showSidebar?: boolean;
  orgName?: string;
  message?: React.ReactNode;
  messageVariant?: MessageVariant;
  data?: T;
  breadcrumbs?: ReactNode[];
  locationHeader?: LocationHeaderProps;
  withOldSidebar?: boolean;
  hasFullPageLayout?: boolean;
  maxWidth?: string;
};

const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  float: 'right',
  height: '85vh',
  margin: 'auto',
  zIndex: 1,
  backgroundColor: theme.palette.misc.background,
  fontFamily: TENANT.theme.modal.fontFamily,
  transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

  '.MuiButton-root': {
    boxSizing: 'border-box',
  },

  '.MuiInput-input': {
    boxSizing: 'content-box',
  },
}));

export const LoadingBox = styled(Box)<{ hasFullPageLayout?: boolean }>(
  ({ hasFullPageLayout }) => ({
    height: hasFullPageLayout ? '100vh' : '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '20px',
  }),
);

const Page = <T extends object>({
  sx,
  children,
  isLoading,
  orgName,
  topBar,
  showSidebar,
  message,
  messageVariant,
  data,
  breadcrumbs,
  withOldSidebar,
  locationHeader,
  hasFullPageLayout,
  maxWidth = '1920px',
}: Props<T>) => {
  const { isVisibleDisputes, restrictionDate } = useDisputesData();
  const { data: user } = useGetUserFromToken();
  const { isExpanded } = useSidebarStore();

  const isPausedAccount = user?.data.daoi?.accountStatus === 5;

  let sidebarWidth =
    showSidebar &&
    (isExpanded ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH);

  if (withOldSidebar) {
    sidebarWidth = OLD_SIDEBAR_WIDTH;
  }

  let topbarHeight = topBar && TOPBAR_HEIGHT;

  if (!!breadcrumbs || !!locationHeader) {
    topbarHeight = BREADCRUMBS_HEIGHT;
  }

  const hasData = !!data;
  let content = children;

  if (message) {
    content = (
      <PageMessageTemplate
        message={message}
        variant={messageVariant}
        hasFullPageLayout={hasFullPageLayout}
      />
    );
  } else if (isLoading) {
    content = hasFullPageLayout ? (
      <LoadingBox hasFullPageLayout>
        <CircularProgress />
      </LoadingBox>
    ) : (
      <PageCardTemplate>
        <LoadingBox>
          <CircularProgress />
        </LoadingBox>
      </PageCardTemplate>
    );
  } else if (isLoading === false && !hasData) {
    content = (
      <PageMessageTemplate
        message={
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography
              fontSize="1.375rem"
              textAlign="center"
              fontWeight={700}
              marginBottom={2}
            >
              OOPS, SOMETHING WENT WRONG
              <br /> Please verify success or try again.
            </Typography>
            <Button
              color="primary"
              variant="contained"
              onClick={() => window.location.reload()}
            >
              REFRESH PAGE
            </Button>
          </Box>
        }
        variant="error"
        hasFullPageLayout={hasFullPageLayout}
      />
    );
  }
  return (
    <ScopedCssBaseline>
      {window.location.href.includes('account/') && (
        <GlobalBanner isExpanded={isExpanded} />
      )}
      {isPausedAccount && (
        <ModalForceLogOut
          isPausedAccount={isPausedAccount}
          restrictionDate={
            isVisibleDisputes && restrictionDate ? restrictionDate : undefined
          }
        />
      )}
      <PageLayout
        topBar={topBar}
        showSidebar={showSidebar}
        orgName={orgName}
        isExpanded={isExpanded}
        breadcrumbs={breadcrumbs}
        locationHeader={locationHeader}
        maxWidth={maxWidth}
      >
        <PageContainer
          sx={sx}
          style={{
            width: `calc(100% - ${sidebarWidth}px)`,
            minHeight: `calc(100% - ${topbarHeight}px)`,
          }}
        >
          {typeof content === 'function' && hasData ? content(data) : content}
        </PageContainer>
      </PageLayout>
    </ScopedCssBaseline>
  );
};

export { Page };
